import type { CostCenter } from '../types';

/**
 * Primeira letra maiúscula, demais minúsculas (pt-BR).
 * Ex.: "MOLDADOR DE PLÁSTICO" → "Moldador de plástico"
 */
export function sentenceCasePtBr(text: string): string {
  if (!text) return '';
  const trimmed = text.trim();
  if (!trimmed) return '';
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
}

/**
 * Sinônimos/abreviações vindos do PDF RH → nome canônico cadastrado em Configurações.
 * Chaves já em sentenceCasePtBr para lookup estável.
 */
const EMPLOYEE_ROLE_ALIASES: Record<string, string> = {
  'Op. especializado de extrusora': 'Operador especializado de extrusora',
  'Operador extrusora de plástico': 'Operador de extrusora de plástico',
  'Operador extrusora de plastico': 'Operador de extrusora de plástico',
  'Extrusor de plastico': 'Extrusor de plástico',
  'Inspetor(a) qualidade': 'Inspetor (a) de qualidade',
  'Inspetor (a) qualidade': 'Inspetor (a) de qualidade',
  'Motorista de caminhao leve': 'Motorista de caminhão leve',
  'Motorista de caminhão leve': 'Motorista de caminhão leve',
};

/**
 * Converte função do colaborador para o nome canônico usado no select de Configurações.
 * Evita `<select>` com valor fora da lista (browser exibe opção errada).
 * @param role - Função bruta (PDF, legado ou editada)
 * @param availableRoles - Funções cadastradas; quando informado, prioriza match exato
 * @returns Nome canônico ou função normalizada em sentence case
 */
export function canonicalizeEmployeeRole(role: string, availableRoles?: string[]): string {
  const normalized = sentenceCasePtBr(role);
  if (!normalized) return '';

  if (availableRoles?.includes(normalized)) return normalized;

  const aliased = EMPLOYEE_ROLE_ALIASES[normalized];
  if (aliased) return aliased;

  if (availableRoles?.length) {
    const caseInsensitive = availableRoles.find(
      (candidate) => candidate.toLowerCase() === normalized.toLowerCase(),
    );
    if (caseInsensitive) return caseInsensitive;
  }

  return normalized;
}

/** Lista única de funções, ordenada. */
export function normalizeRolesList(roles: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const r of roles) {
    const n = canonicalizeEmployeeRole(r);
    if (n && !seen.has(n)) {
      seen.add(n);
      out.push(n);
    }
  }
  return out.sort((a, b) => a.localeCompare(b, 'pt-BR'));
}

export function normalizeCostCentersList(centers: CostCenter[]): CostCenter[] {
  const mapped = centers.map((c) => ({
    code: String(c.code).trim(),
    description: sentenceCasePtBr(c.description),
  }));
  return mapped.sort((a, b) => a.code.localeCompare(b.code, 'pt-BR', { numeric: true }));
}

/** Reaponta chaves de cores para o nome normalizado/canônico da função. */
export function normalizeRoleColorsMap(colors: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(colors)) {
    const nk = canonicalizeEmployeeRole(k);
    if (nk) out[nk] = v;
  }
  return out;
}
